import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Youtube, Plus, LogOut, Loader2, Send, MessageSquare, BarChart3, PlugZap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SEO from "@/components/SEO";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { waLink } from "@/lib/whatsapp";

const STATUS_COLORS = {
  Pending: "bg-slate-100 text-slate-700", Reviewing: "bg-amber-100 text-amber-700",
  Approved: "bg-blue-100 text-blue-700", Active: "bg-green-100 text-green-700",
  Paused: "bg-orange-100 text-orange-700", Completed: "bg-emerald-100 text-emerald-700",
  Cancelled: "bg-red-100 text-red-700",
};

export default function ClientDashboard() {
  const { user, logout, setUser } = useAuth();
  const nav = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", youtube_url: "", objective: "", target_country: "", target_audience: "", budget: "" });
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({ name: user?.name || "", company: user?.company || "", phone: user?.phone || "" });
  const [msg, setMsg] = useState("");

  const load = () => api.get("/client/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Campaign name is required."); return; }
    setLoading(true);
    try { await api.post("/client/campaigns", form); toast.success("Campaign request submitted!"); setOpen(false); setForm({ name: "", youtube_url: "", objective: "", target_country: "", target_audience: "", budget: "" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const saveProfile = async () => {
    try { const { data } = await api.patch("/client/profile", profile); setUser({ ...user, ...data }); toast.success("Profile updated."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try { await api.post("/client/support", { message: msg }); toast.success("Message sent to support."); setMsg(""); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="Client Dashboard | NewStarAds" description="Manage your YouTube campaigns." />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-extrabold"><span className="grid place-items-center w-8 h-8 rounded-lg bg-primary text-white"><Youtube className="w-5 h-5" /></span> NewStarAds</Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:inline">Hi, {user?.name}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="client-logout"><LogOut className="w-4 h-4 mr-1" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10">
        <Tabs defaultValue="campaigns">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div><h1 className="font-display text-3xl font-bold tracking-tight">Welcome back</h1><p className="text-slate-500">Manage your YouTube campaigns and account.</p></div>
            <TabsList><TabsTrigger value="campaigns">Campaigns</TabsTrigger><TabsTrigger value="reports">Reports</TabsTrigger><TabsTrigger value="support">Support</TabsTrigger><TabsTrigger value="profile">Profile</TabsTrigger></TabsList>
          </div>

          <TabsContent value="campaigns">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-xl font-bold">Your campaigns ({campaigns.length})</h2>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button className="rounded-full" data-testid="client-new-campaign"><Plus className="w-4 h-4 mr-1" /> Request campaign</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Request a new campaign</DialogTitle></DialogHeader>
                  <form onSubmit={submit} className="space-y-4">
                    <div className="space-y-1.5"><Label>Campaign name *</Label><Input data-testid="campaign-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
                    <div className="space-y-1.5"><Label>YouTube URL</Label><Input value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Objective</Label><Input value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>Budget</Label><Input value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Target country</Label><Input value={form.target_country} onChange={(e) => setForm({ ...form, target_country: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>Target audience</Label><Input value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} /></div>
                    </div>
                    <DialogFooter><Button type="submit" disabled={loading} data-testid="campaign-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit request"}</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {campaigns.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center text-slate-400">No campaigns yet. Request your first campaign above.</div>
            ) : (
              <div className="grid gap-4">
                {campaigns.map((c) => (
                  <div key={c.id} data-testid={`client-campaign-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-6">
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <h3 className="font-display text-lg font-bold">{c.name}</h3>
                        {c.youtube_url && <a href={c.youtube_url} target="_blank" rel="noreferrer" className="text-sm text-primary break-all">{c.youtube_url}</a>}
                        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-500">
                          {c.objective && <span>Objective: {c.objective}</span>}
                          {c.budget && <span>Budget: {c.budget}</span>}
                          {c.target_country && <span>Country: {c.target_country}</span>}
                        </div>
                        {c.notes && <p className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{c.notes}</p>}
                      </div>
                      <Badge className={STATUS_COLORS[c.status] || "bg-slate-100 text-slate-700"}>{c.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reports">
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="client-reports-empty">
              <div className="mx-auto w-14 h-14 grid place-items-center rounded-full bg-primary/10 text-primary mb-4"><PlugZap className="w-7 h-7" strokeWidth={1.5} /></div>
              <h2 className="font-display text-xl font-bold">Connect Analytics to see reports</h2>
              <p className="mt-2 text-slate-500 max-w-md mx-auto text-sm">Your campaign reports will appear here with <b>real, verified data</b> once your YouTube Analytics / Google Ads account is connected by our team. We never show fake or estimated numbers.</p>
              <a href={waLink("918382890707", "Hi NewStarAds, I'd like to connect my analytics to see campaign reports.")} target="_blank" rel="noreferrer">
                <Button className="mt-6 rounded-full" data-testid="client-connect-analytics"><MessageSquare className="w-4 h-4 mr-1" /> Request analytics connection</Button>
              </a>
            </div>
          </TabsContent>

          <TabsContent value="support">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-xl">
              <div className="flex items-center gap-2 mb-4"><MessageSquare className="w-5 h-5 text-primary" /><h2 className="font-display text-lg font-bold">Message support</h2></div>
              <Textarea rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="How can we help?" data-testid="support-message" />
              <Button className="mt-4 rounded-full" onClick={sendMsg} data-testid="support-send"><Send className="w-4 h-4 mr-1" /> Send</Button>
            </div>
          </TabsContent>

          <TabsContent value="profile">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-xl space-y-4">
              <div className="space-y-1.5"><Label>Name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Company</Label><Input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Phone</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Email</Label><Input value={user?.email} disabled /></div>
              <Button className="rounded-full" onClick={saveProfile} data-testid="profile-save">Save profile</Button>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
